/* Button.
 *
 * Same variants, sizes and hover behaviour as the previous library, so the
 * console and this app stay visually identical while both exist.
 */
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // `select-none` matters on a feed: a mis-drag on Like should not highlight
  // the label. `active:scale` gives the press a physical feel on touch.
  'inline-flex items-center justify-center gap-s2 rounded font-semibold ' +
    'select-none whitespace-nowrap transition-colors duration-DEFAULT ' +
    'disabled:pointer-events-none ' +
    'focus-visible:outline-none focus-visible:shadow-focus active:scale-[.98]',
  {
    variants: {
      // DISABLED IS PER-VARIANT, not one global `opacity-50`. Half-opacity
      // over a saturated brand blue lands on a pale blue that still reads as a
      // live button, so people click it and nothing happens. A filled button
      // drains to grey; a transparent one (a feed action) has no fill to
      // drain, so it dims its text instead.
      variant: {
        primary:
          'bg-brand text-ink-on-brand hover:bg-brand-hover active:bg-brand-active ' +
          'disabled:bg-hover-2 disabled:text-ink-3',
        // A hairline and a very light warm fill, not the old mid-grey. A
        // secondary button should read as available, not as a disabled
        // primary — and on a warm background a grey slab goes muddy.
        secondary:
          'border border-line-2 bg-surface-2 text-ink hover:bg-hover ' +
          'disabled:bg-hover disabled:text-ink-3',
        ghost: 'bg-transparent text-ink-2 hover:bg-hover disabled:text-ink-3',
        outline:
          'border border-line bg-surface text-ink hover:bg-hover ' +
          'disabled:bg-hover disabled:text-ink-3',
        danger:
          'bg-danger text-white hover:opacity-90 focus-visible:shadow-focus-danger ' +
          'disabled:bg-hover-2 disabled:text-ink-3',
        // A feed action: Like, Comment, Share. Wide, quiet, fills its third.
        action:
          'flex-1 bg-transparent text-ink-2 font-semibold hover:bg-hover ' +
          'disabled:text-ink-3',
      },
      size: {
        sm: 'h-9 px-s3 text-sm',
        md: 'h-[42px] px-s4 text-base',
        lg: 'h-[50px] px-s5 text-lg font-bold',
        icon: 'h-10 w-10 p-0',
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, asChild, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, full }), className)}
        // A loading button must not be clickable twice — that is how duplicate
        // posts get created.
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {/* asChild renders through Radix's Slot, which accepts EXACTLY ONE
            child — and a spinner beside {children} makes two, even when the
            spinner is null, which crashes the whole tree rather than that one
            button. A link-shaped button has nothing to load anyway: it
            navigates, it does not submit. So the spinner is for real buttons
            only. */}
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {children}
          </>
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
